import React from "react";
import { ReactComponent as MapIcon } from "assets/images/map.svg";
import { ReactComponent as PlaneUpIcon } from "assets/images/plane-up.svg";
import { ReactComponent as ScanIcon } from "assets/images/scan-ticket.svg";
import { ReactComponent as SmartGateIcon } from "assets/images/smart-pre-check.svg";
import { Box, Text } from "components";
import useIsPWA from "hooks/useIsPWA";
import useTrackPageAnalytics from "hooks/useTrackPageAnalytics";
import useWindowDimensions from "hooks/windowDimensions";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetSettingsQuery } from "redux/queries/settings.query";
import { setWayfingModalOpen } from "redux/slices/modals.slice";
import { useTheme } from "styled-components";

const ActionsMenu = () => {
  const { sendAnalytics } = useTrackPageAnalytics();
  const isPWA = useIsPWA();

  const { data: settingsData = {} } = useGetSettingsQuery(undefined, {
    refetchOnReconnect: true,
  });

  const { featureMap: featureFlags } = settingsData;

  const { width } = useWindowDimensions();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  return hasOnlyScanTicketFeature(featureFlags) ? (
    <ScanTicketMenu />
  ) : (
    <Box
      w="100%"
      h={isPWA ? "92px" : "80px"}
      d="flex"
      alignItems="center"
      position="fixed"
      bottom="0"
      backgroundColor="accent_1"
      style={{ zIndex: theme.zIndex.above }}
    >
      <Box
        w="100%"
        d="flex"
        justifyContent="flex-start"
        alignItems="center"
        pT={width < 399 ? "xxs" : "xs"}
      >
        {featureFlags?.scan && (
          <Box
            d="flex"
            direction="column"
            justifyContent="space-between"
            alignItems="center"
            gap="xxs"
            mV="base"
            flex="1"
            onClick={() => {
              sendAnalytics({
                key: "scanner-click",
                type: "event",
                name: "Clicked on Scanner in Main Menu",
              });
              navigate("/scan-ticket");
            }}
            minH="54px"
            pB={isPWA ? "s" : "unset"}
          >
            <PlaneUpIcon
              width={26.5}
              height={26.5}
              color={theme.colors.textQuaternary}
            />
            <Box h="30px" d="flex" alignItems="center">
              <Text fontWeight="bold" color="textQuaternary" textAlign="center">
                {t("header.scanner")}
              </Text>
            </Box>
          </Box>
        )}
        {featureFlags?.smart_passports &&
          !featureFlags?.hide_smart_passports_in_menu && (
            <Box
              d="flex"
              direction="column"
              justifyContent="space-between"
              alignItems="center"
              gap="xxs"
              mV="base"
              flex="1"
              onClick={() => navigate("/scan-passport?source=click")}
              minH="54px"
              pB={isPWA ? "s" : "unset"}
            >
              <SmartGateIcon width={32} height={32} />
              <Box h="30px" d="flex" alignItems="center">
                <Text
                  fontWeight="bold"
                  color="textQuaternary"
                  textAlign="center"
                >
                  {t("header.smart_gate")}
                </Text>
              </Box>
            </Box>
          )}
        {featureFlags?.wayfinding && (
          <Box
            d="flex"
            direction="column"
            justifyContent="space-between"
            alignItems="center"
            gap="xxs"
            mV="base"
            flex="1"
            style={{
              cursor: "pointer",
            }}
            onClick={() => {
              sendAnalytics({
                key: "wayfinding-click",
                type: "event",
                name: "Clicked on Wayfinding in Main Menu",
              });
              dispatch(setWayfingModalOpen(true));
            }}
            minH="54px"
            pB={isPWA ? "s" : "unset"}
          >
            <MapIcon width={32} height={32} />
            <Box h="30px" d="flex" alignItems="center">
              <Text fontWeight="bold" color="textQuaternary" textAlign="center">
                {t("header.airport_map")}
              </Text>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

function ScanTicketMenu() {
  const theme = useTheme();
  const { sendAnalytics } = useTrackPageAnalytics();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box
      w="100%"
      h="80px"
      d="flex"
      justifyContent="center"
      alignItems="center"
      position="fixed"
      bottom="0"
      style={{ zIndex: theme.zIndex.above }}
      onClick={() => {
        sendAnalytics({
          key: "scanner-click",
          type: "event",
          name: "Clicked on Scanner in Main Menu",
        });
        navigate("/scan-ticket");
      }}
    >
      <Box
        w="70%"
        pV="xs"
        d="flex"
        justifyContent="center"
        alignItems="center"
        backgroundColor="accent_1"
        color={theme.colors.textQuaternary}
        style={{ borderRadius: "50px" }}
      >
        <ScanIcon width={24} height={24} />
        <Text fontWeight="bold" color="textQuaternary" mH="s">
          {t("header.scanner_long")}
        </Text>
      </Box>
    </Box>
  );
}

function hasOnlyScanTicketFeature(featureFlags) {
  return (
    featureFlags?.scan &&
    !featureFlags?.smart_passports &&
    !featureFlags?.wayfinding
  );
}

export default ActionsMenu;
